<template>
  <div class="flex flex-col gap-4 sm:gap-8 xl:flex-row">
    <!-- Left column: Form content -->
    <div class="flex-1 rounded-lg bg-white p-3 sm:p-5 shadow-sm sm:p-6">
      <!-- Top row: Start Date, End Date, Poll Scheduler -->
      <div
        class="mb-4 sm:mb-6 grid grid-cols-1 gap-2 sm:gap-4 lg:grid-cols-2 lg:grid-rows-2"
      >
        <div
          class="relative lg:col-start-1 lg:row-start-1"
          ref="startDateWrapper"
        >
          <button
            class="flex h-10 sm:h-14 w-full items-center justify-between gap-2 sm:gap-3 rounded-lg border border-gray-200 bg-gray-50 px-3 sm:px-4 text-left text-sm sm:text-base transition-colors hover:bg-gray-100 focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary"
            @click="toggleDatePicker('start')"
          >
            <span class="text-gray-600 whitespace-nowrap truncate">
              {{ showPollStartTime || "Start Date" }}
            </span>
            <svg
              class="w-4 h-4 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                d="M19 9l-7 7-7-7"
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
              />
            </svg>
          </button>
          <div
            v-if="showStartDatePicker"
            class="absolute left-0 top-full z-10 mt-1 w-full max-w-md"
          >
            <DateTimePicker
              v-model:dateDisplay="showPollStartTime"
              :hide-button="true"
              title="Poll Start"
              @applyDateTime="handleApplyDateTime"
              @dateDisplay="showPollStartTime"
              @isCalendarOpen="handleStartDateCalendarClosed"
            />
          </div>
        </div>
        <div
          class="relative lg:col-start-1 lg:row-start-2"
          ref="endDateWrapper"
        >
          <button
            class="flex h-10 sm:h-14 w-full items-center justify-between gap-2 sm:gap-3 rounded-lg border border-gray-200 bg-gray-50 px-3 sm:px-4 text-left text-sm sm:text-base transition-colors hover:bg-gray-100 focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary"
            @click="toggleDatePicker('end')"
          >
            <span class="text-gray-600 whitespace-nowrap truncate">
              {{ showPollEndTime || "End Date" }}
            </span>
            <svg
              class="w-4 h-4 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                d="M19 9l-7 7-7-7"
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
              />
            </svg>
          </button>
          <div
            v-if="showEndDatePicker"
            class="absolute left-0 top-full z-10 mt-1 w-full max-w-md"
          >
            <DateTimePicker
              v-model:dateDisplay="showPollEndTime"
              :hide-button="true"
              title="Poll End"
              @applyDateTime="handleDateTimeForPollDuration"
              @dateDisplay="showPollEndTime"
              @isCalendarOpen="handleEndDateCalendarClosed"
            />
          </div>
        </div>
        <div
          class="relative lg:col-start-2 lg:row-start-2"
          ref="schedulerWrapper"
        >
          <div
            v-if="showSchedulerPicker"
            class="fixed inset-0 z-40"
            @click="showSchedulerPicker = false"
          ></div>
          <button
            class="flex h-10 sm:h-14 w-full items-center justify-between gap-2 sm:gap-3 rounded-lg border border-gray-200 bg-gray-50 px-3 sm:px-4 text-left text-sm sm:text-base transition-colors hover:bg-gray-100 focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary"
            @click="toggleDatePicker('scheduler')"
          >
            <span class="text-gray-600 whitespace-nowrap truncate">
              Poll Scheduler
            </span>
            <svg
              class="w-4 h-4 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                d="M19 9l-7 7-7-7"
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
              />
            </svg>
          </button>
          <div
            v-if="showSchedulerPicker"
            class="absolute left-0 top-full z-50 mt-1 w-full max-w-md"
          >
            <RepeatSchedulerPicker
              :hide-button="true"
              @isWindowOpen="handleSchedulerClosed"
              @settingsApplied="handleScheduler"
            />
          </div>
        </div>
      </div>

      <!-- Poll Name and Space -->
      <div class="mb-4 sm:mb-6 grid grid-cols-1 gap-3 sm:gap-4 md:grid-cols-2">
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2"
            >Poll Name</label
          >
          <input
            v-model="pollName"
            :class="[
              'w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-200 rounded-lg focus:ring-1 focus:ring-primary focus:border-primary focus:outline-none text-sm sm:text-base',
              pollNameValidation ? 'border-red-500' : '',
            ]"
            placeholder="Enter Poll Name"
            type="text"
          />
          <small class="text-red-500">{{ pollNameValidation }}</small>
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">
            {{ pollType === "individual" ? "Members" : "Space" }}
          </label>
          <div class="relative" ref="selectorWrapper">
            <button
              :class="[
                'w-full flex items-center justify-between px-3 sm:px-4 py-2 sm:py-3 border rounded-lg text-left text-sm sm:text-base transition-colors focus:ring-1 focus:ring-primary focus:border-primary focus:outline-none',
                selectedOptionsValidation
                  ? 'border-red-500 bg-red-50'
                  : 'bg-gray-50 border-gray-200 hover:bg-gray-100',
              ]"
              @click="toggleSelector"
            >
              <span class="text-gray-600">
                {{ getSelectedText() }}
              </span>
              <svg
                :class="[
                  'w-4 h-4 text-gray-400 transition-transform',
                  showSelector ? 'rotate-180' : '',
                ]"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  d="M19 9l-7 7-7-7"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                />
              </svg>
            </button>
            <div
              v-if="showSelector"
              class="absolute top-full left-0 z-20 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto"
            >
              <div
                v-for="option in options"
                :key="option.id"
                :class="[
                  'cursor-pointer transition-colors border-b border-gray-100 last:border-b-0 hover:bg-gray-50',
                  option.id === 'selectAll' ? 'px-4 py-2' : 'px-4 py-3',
                  isOptionSelected(option) && option.id !== 'selectAll'
                    ? 'bg-gray-50 text-gray-900'
                    : 'text-gray-700',
                ]"
                @click="handleOptionSelect(option)"
              >
                <div class="flex items-center justify-between">
                  <span class="font-medium">{{ option.name }}</span>
                  <div
                    v-if="isOptionSelected(option)"
                    class="flex items-center"
                  >
                    <svg
                      class="w-4 h-4 text-green-600"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        clip-rule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        fill-rule="evenodd"
                      />
                    </svg>
                  </div>
                </div>
                <div
                  v-if="option.id === 'selectAll'"
                  class="text-sm text-gray-500 mt-1"
                >
                  Press enter to select
                </div>
              </div>
            </div>
          </div>
          <small class="text-red-500">{{ selectedOptionsValidation }}</small>
        </div>
      </div>

      <!-- Anonymous checkbox -->
      <div class="mb-4 sm:mb-6 flex items-start gap-2 sm:gap-3 sm:items-center">
        <input
          id="anonymous"
          v-model="isAnonymous"
          class="w-4 h-4 bg-gray-100 border-gray-300 rounded focus:ring-1 focus:ring-primary accent-primary"
          type="checkbox"
        />
        <label class="ml-2 text-sm text-gray-700" for="anonymous"
          >Anonymous</label
        >
      </div>
      <!-- Question Section -->
      <div class="mb-4 sm:mb-6">
        <label class="block text-sm font-medium text-gray-700 mb-2"
          >Question</label
        >

        <div
          v-for="(option, index) in question"
          :key="index"
          class="mb-3 sm:mb-4"
        >
          <!-- Radio button question type -->
          <div v-if="option && option.type === FieldType.RADIO" class="w-full">
            <!-- Input field for the question text -->
            <div
              class="mb-2 sm:mb-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3"
            >
              <input
                v-model="option.text"
                :class="[
                  'flex-1 px-3 sm:px-4 py-2 sm:py-3 border border-gray-200 rounded-lg focus:ring-1 focus:ring-primary focus:border-primary focus:outline-none text-sm sm:text-base',
                  radioValidationMessages[index] ? 'border-red-500' : '',
                ]"
                :placeholder="'Enter Poll Name'"
                type="text"
              />
              <!-- Add Value and Remove Field buttons -->
              <button
                class="rounded text-gray-700 hover:text-gray-900 focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary"
                @click="addValue(index)"
              >
                <svg
                  class="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                  />
                </svg>
              </button>
              <button
                class="rounded text-red-500 hover:text-red-600 focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary"
                @click="removeOption(index)"
              >
                <svg
                  class="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    d="M6 18L18 6M6 6l12 12"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                  />
                </svg>
              </button>
            </div>
            <div class="mb-2">
              <small class="text-red-500">{{
                radioValidationMessages[index] ? radioErrorMessage : ""
              }}</small>
            </div>
            <!-- Values for the radio button question -->
            <div
              v-for="(value, vIndex) in option.values"
              :key="vIndex"
              class="mb-2 flex flex-col gap-2 sm:flex-row sm:items-center"
            >
              <input
                v-model="option.values[vIndex]"
                :class="[
                  'flex-1 px-3 sm:px-4 py-1.5 sm:py-2 border border-gray-200 rounded-lg focus:ring-1 focus:ring-primary focus:border-primary focus:outline-none text-sm sm:text-base',
                  radioValidationMessages[index] ? 'border-red-500' : '',
                ]"
                :placeholder="'Option ' + (vIndex + 1)"
                type="text"
              />
              <button
                class="rounded text-red-500 hover:text-red-600 focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary"
                @click="removeValue(index, vIndex)"
              >
                <svg
                  class="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    d="M6 18L18 6M6 6l12 12"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                  />
                </svg>
              </button>
            </div>
          </div>

          <!-- Text field input for adding new text fields -->
          <div
            v-else-if="option && option.type === FieldType.TEXT"
            class="w-full"
          >
            <div class="mb-2 flex flex-col gap-2 sm:flex-row sm:items-center">
              <input
                v-model="option.text"
                :class="[
                  'flex-1 px-3 sm:px-4 py-2 sm:py-3 border border-gray-200 rounded-lg focus:ring-1 focus:ring-primary focus:border-primary focus:outline-none text-sm sm:text-base',
                  textFieldValidationMessages[index] ? 'border-red-500' : '',
                ]"
                :placeholder="'Question ' + (index + 1)"
                type="text"
              />
              <button
                class="rounded text-red-500 hover:text-red-600 focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary"
                @click="removeOption(index)"
              >
                <svg
                  class="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    d="M6 18L18 6M6 6l12 12"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                  />
                </svg>
              </button>
            </div>
            <small class="text-red-500">{{
              textFieldValidationMessages[index] ? textErrorMessage : ""
            }}</small>
          </div>
        </div>
      </div>

      <!-- Add buttons -->
      <div
        class="mb-4 sm:mb-8 flex flex-row gap-2 sm:flex-row sm:flex-wrap sm:gap-4"
      >
        <button
          class="flex flex-1 sm:w-auto sm:flex-initial items-center justify-center gap-1 sm:gap-2 rounded-lg border border-gray-300 bg-white px-3 sm:px-4 py-2 text-xs sm:text-sm text-gray-600 transition-colors hover:bg-gray-50 focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary"
          @click="addRadioButton"
        >
          <svg
            class="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              d="M12 6v6m0 0v6m0-6h6m-6 0H6"
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
            />
          </svg>
          <span class="hidden sm:inline">Add Multi-Choice</span>
          <span class="sm:hidden">Multi-Choice</span>
        </button>
        <button
          class="flex flex-1 sm:w-auto sm:flex-initial items-center justify-center gap-1 sm:gap-2 rounded-lg border border-gray-300 bg-white px-3 sm:px-4 py-2 text-xs sm:text-sm text-gray-600 transition-colors hover:bg-gray-50 focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary"
          @click="addTextField"
        >
          <svg
            class="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              d="M12 6v6m0 0v6m0-6h6m-6 0H6"
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
            />
          </svg>
          <span class="hidden sm:inline">Add Text-Area</span>
          <span class="sm:hidden">Text-Area</span>
        </button>
      </div>

      <!-- Bottom action buttons -->
      <div
        class="flex flex-row gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-3"
      >
        <button
          class="flex-1 sm:flex-initial rounded-lg border border-gray-300 bg-white px-4 sm:px-6 py-2 text-sm sm:text-base text-gray-700 transition-colors hover:bg-gray-50 focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary"
          @click="resetPollForm"
        >
          Reset
        </button>
        <button
          class="flex-1 sm:flex-initial rounded-lg bg-btn-gradient px-6 sm:px-8 py-2 text-sm sm:text-base text-white transition-opacity hover:opacity-90 focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary"
          @click="submitPoll"
        >
          Submit
        </button>
      </div>
    </div>

    <!-- Right column: Always visible preview -->
    <div class="h-fit rounded-lg bg-white p-3 sm:p-5 shadow-sm sm:p-6 xl:w-96">
      <div class="text-center mb-4 sm:mb-6">
        <h2
          class="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4"
        >
          {{ pollName || "Poll Title" }}
        </h2>

        <!-- Display questions based on current form data -->
        <div v-if="question.length > 0" class="space-y-3 sm:space-y-6">
          <div v-for="(q, index) in question" :key="index" class="text-left">
            <div v-if="q && q.type === FieldType.TEXT" class="mb-3 sm:mb-4">
              <p class="mb-2 sm:mb-3 text-sm sm:text-base text-gray-700">
                {{ q.text || "Question " + (index + 1) }}
              </p>
              <div
                class="bg-gray-50 border border-gray-200 rounded-lg p-2 sm:p-3"
              >
                <span class="text-gray-400 text-xs sm:text-sm">Free zone</span>
              </div>
            </div>

            <div
              v-else-if="q && q.type === FieldType.RADIO"
              class="mb-3 sm:mb-4"
            >
              <p class="mb-2 sm:mb-3 text-sm sm:text-base text-gray-700">
                {{ q.text || "Question " + (index + 1) }}
              </p>
              <div class="space-y-1 sm:space-y-2">
                <label
                  v-for="(value, vIndex) in q.values"
                  :key="vIndex"
                  class="flex items-center p-1.5 sm:p-2 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer"
                >
                  <input
                    :name="'preview_question_' + index"
                    :value="value"
                    class="mr-2 sm:mr-3 text-primary"
                    disabled
                    type="radio"
                  />
                  <span class="text-sm sm:text-base text-gray-700">{{
                    value || "Option " + (vIndex + 1)
                  }}</span>
                </label>
              </div>
            </div>
          </div>
        </div>

        <!-- Default preview when no questions -->
        <div v-else class="space-y-4 text-gray-400">
          <p class="text-sm">Add questions to see preview</p>
        </div>

        <button
          class="mt-4 sm:mt-6 w-full rounded-lg bg-btn-gradient py-2 px-3 sm:px-4 text-sm sm:text-base text-white transition-opacity hover:opacity-90 focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary disabled:opacity-50"
          disabled
        >
          Submit
        </button>
      </div>
    </div>
  </div>
  <div class="allert-message-width">
    <AlertMessage
      :alertType="alertType ? alertType : 'empty'"
      :message="alertMessage"
      @close="handleClose"
    />
  </div>
</template>
<script>
import {
  createQuestionnaire,
  createScheduledQuestionnaire,
} from "../services/questionnaire";
import { DateTime } from "luxon";
import DateTimePicker from "../components/DateTimePicker.vue";
import RepeatSchedulerPicker from "../components/RepeatSchedulerPicker.vue";
import AlertMessage from "../components/AlertMessage.vue";
import { getTargetUsers } from "../services/targetUsers";
import { getAllSpaces } from "../services/space";

const FieldType = {
  TEXT: "text",
  RADIO: "radio",
};

export default {
  props: {
    pollType: {
      type: String,
      default: "group",
    },
  },
  data() {
    return {
      pollName: "",
      isAnonymous: true,
      pollEndAt: new Date().toISOString(),
      question: [],
      userTimeZone: null,
      scheduledAt: new Date().toISOString(),
      isScheduleCalendarSet: false,
      alertMessage: "",
      alertType: "",
      submitted: false,
      textFieldValidationMessages: [],
      radioValidationMessages: [],
      selectedOptions: [],
      options: [],
      FieldType: FieldType,
      pollNameValidation: "",
      selectedOptionsValidation: "",
      showPollEndTime: "",
      showPollStartTime: "",
      textErrorMessage: "",
      radioErrorMessage: "",
      imageUrl: require("@/assets/images/gpolls.png"),
      spaces: [],
      settings: null,
      scheduledPoll: "",
      showStartDatePicker: false,
      showEndDatePicker: false,
      showSchedulerPicker: false,
      showSelector: false,
    };
  },
  watch: {
    pollType: {
      handler() {
        this.options = [];
        this.selectedOptions = [];
      },
      immediate: true,
    },
    question: {
      handler() {
        this.question.forEach((option, index) => {
          if (option.type === this.FieldType.TEXT) {
            if (option.text.trim()) {
              this.textFieldValidationMessages[index] = false;
              this.textErrorMessage = "";
            }
          }
          if (option.type === this.FieldType.RADIO) {
            if (option.text.trim()) {
              this.radioValidationMessages[index] = false;
              this.radioErrorMessage = "";
            }
          }
        });
      },
      deep: true,
    },
    pollName: {
      handler() {
        if (this.pollName.trim()) {
          this.pollNameValidation = "";
        }
      },
    },
    selectedOptions: {
      handler() {
        if (this.selectedOptions.length > 0) {
          this.selectedOptionsValidation = "";
        }
      },
    },
  },
  components: {
    DateTimePicker,
    AlertMessage,
    RepeatSchedulerPicker,
  },
  mounted() {
    this.getTargetUsers();
    this.detectUserTimeZone();
    this.getSpaces();
    document.addEventListener("click", this.handleClickOutside);
  },
  beforeUnmount() {
    document.removeEventListener("click", this.handleClickOutside);
  },
  methods: {
    resetPollForm() {
      this.pollName = "";
      this.selectedOptions = [];
      this.question = [];
      this.isAnonymous = true;
      this.pollEndAt = new Date().toISOString();
      this.scheduledAt = new Date().toISOString();
      this.isScheduleCalendarSet = false;
      this.showPollEndTime = "";
      this.showPollStartTime = "";
    },
    handleScheduler(value) {
      this.scheduledPoll = value;
    },
    handleClickOutside(event) {
      const target = event.target;
      const interactiveAreas = [
        this.$refs.startDateWrapper,
        this.$refs.endDateWrapper,
        this.$refs.schedulerWrapper,
        this.$refs.selectorWrapper,
      ];

      const clickedInside = interactiveAreas.some(
        (area) => area && area.contains(target)
      );

      if (!clickedInside) {
        this.showStartDatePicker = false;
        this.showEndDatePicker = false;
        this.showSchedulerPicker = false;
        this.showSelector = false;
      }
    },
    toggleDatePicker(type) {
      // Close all dropdowns first
      this.showStartDatePicker = false;
      this.showEndDatePicker = false;
      this.showSchedulerPicker = false;

      // Open the selected one
      if (type === "start") {
        this.showStartDatePicker = true;
      } else if (type === "end") {
        this.showEndDatePicker = true;
      } else if (type === "scheduler") {
        this.showSchedulerPicker = true;
      }
    },
    toggleSelector() {
      this.showSelector = !this.showSelector;
      if (this.showSelector) {
        // Close other dropdowns
        this.showStartDatePicker = false;
        this.showEndDatePicker = false;
        this.showSchedulerPicker = false;

        // Load options based on poll type
        if (this.pollType === "individual") {
          this.handleMultiselectOpen();
        } else {
          this.handleMultiselectOpenForSpace();
        }
      }
    },
    getSelectedText() {
      if (this.selectedOptions.length === 0) {
        return this.pollType === "individual"
          ? "Select Members"
          : "Select Space";
      }

      if (this.selectedOptions[0]?.id === "selectAll") {
        return this.pollType === "individual" ? "All Members" : "All Spaces";
      }

      if (this.selectedOptions.length === 1) {
        return this.selectedOptions[0].name;
      }

      return `${this.selectedOptions.length} selected`;
    },
    isOptionSelected(option) {
      return this.selectedOptions.some((selected) => selected.id === option.id);
    },
    handleOptionSelect(option) {
      if (option.id === "selectAll") {
        // Check if "Select All" is currently selected
        const isSelectAllSelected = this.selectedOptions.some(
          (selected) => selected.id === "selectAll"
        );

        if (isSelectAllSelected) {
          // Deselect all - clear the selection
          this.selectedOptions = [];
        } else {
          // Select all - use existing logic
          this.handleSelect(option);
        }
        this.showSelector = false; // Close dropdown for "Select All"
      } else {
        // Check if option is already selected
        const isCurrentlySelected = this.selectedOptions.some(
          (selected) => selected.id === option.id
        );

        if (isCurrentlySelected) {
          // Remove from selection
          this.selectedOptions = this.selectedOptions.filter(
            (selected) => selected.id !== option.id
          );
        } else {
          // Add to selection
          this.handleSelect(option);
        }
        // Keep dropdown open for individual selections
      }
    },
    addTextField() {
      this.question.push({ type: this.FieldType.TEXT, text: "" });
    },
    addRadioButton() {
      this.question.push({
        type: this.FieldType.RADIO,
        text: "",
        values: [""],
      });
    },
    addValue(index) {
      if (this.question[index]) {
        this.question[index].values.push("");
      }
    },
    removeOption(index) {
      this.question.splice(index, 1);
    },
    removeValue(oIndex, vIndex) {
      if (this.question[oIndex] && this.question[oIndex].values) {
        this.question[oIndex].values.splice(vIndex, 1);
      }
    },
    updateText(event, index) {
      if (this.question[index]) {
        this.question[index].text = event.target.value;
      }
    },
    updateValue(event, oIndex, vIndex) {
      if (this.question[oIndex] && this.question[oIndex].values) {
        this.question[oIndex].values[vIndex] = event.target.value;
      }
    },
    detectUserTimeZone() {
      this.userTimeZone = DateTime.utc();
    },
    formatDateTime(dateTimeData) {
      return DateTime.fromISO(dateTimeData)
        .setLocale("en-GB")
        .toFormat("EEE, dd LLL yyyy HH:mm");
    },
    handleDateTimeForPollDuration(dateTimeData) {
      this.pollEndAt = dateTimeData.selectedDateTime;
      this.showPollEndTime = this.formatDateTime(dateTimeData.selectedDateTime);
      this.showEndDatePicker = false;
    },
    handleApplyDateTime(dateTimeData) {
      this.scheduledAt = dateTimeData.selectedDateTime;
      this.showPollStartTime = this.formatDateTime(
        dateTimeData.selectedDateTime
      );
      this.showStartDatePicker = false;
    },
    handleCalendarClosed(isOpen) {
      this.isScheduleCalendarSet = isOpen;
    },
    handleStartDateCalendarClosed(isOpen) {
      this.isScheduleCalendarSet = isOpen;
      if (!isOpen) {
        this.showStartDatePicker = false;
      }
    },
    handleEndDateCalendarClosed(isOpen) {
      if (!isOpen) {
        this.showEndDatePicker = false;
      }
    },
    handleSchedulerClosed(isOpen) {
      if (!isOpen) {
        this.showSchedulerPicker = false;
      }
    },
    handleClose() {
      this.alertMessage = "";
      this.alertType = "";
    },
    getStructuredQuestion(questions) {
      const formattedQuestions = {};
      questions.forEach((q, index) => {
        const key = `question_${index + 1}`;
        if (q && q.type === this.FieldType.TEXT) {
          formattedQuestions[key] = {
            type: this.FieldType.TEXT,
            title: q.text,
          };
        } else if (q && q.type === this.FieldType.RADIO) {
          formattedQuestions[key] = {
            type: this.FieldType.RADIO,
            title: q.text,
            answersOptions: q.values,
          };
        }
      });

      return formattedQuestions;
    },
    async validatePoll(scheduledAt, pollEndAt, questions) {
      if (this.isScheduleCalendarSet && scheduledAt > pollEndAt) {
        return {
          alertMessage: "Poll end date should be greater than scheduled date",
          alertType: "dismiss",
        };
      }
      if (!this.pollName.trim()) {
        return (this.pollNameValidation =
          "Poll Name is required. Please enter a name.");
      } else {
        this.pollNameValidation = "";
      }

      if (questions.length === 0) {
        return {
          alertMessage:
            "Please add questions to submit the poll. Need at least one questionnaire (text field or radio button)",
          alertType: "dismiss",
        };
      }

      this.textFieldValidationMessages = [];
      this.radioValidationMessages = [];
      this.question.forEach((option, index) => {
        if (option.type === this.FieldType.TEXT) {
          if (!option.text.trim()) {
            this.textFieldValidationMessages[index] = true;
          } else {
            this.textFieldValidationMessages[index] = false;
          }
        }
        if (
          option.type === this.FieldType.RADIO &&
          (!option.text.trim() || option.values.some((val) => !val.trim()))
        ) {
          this.radioValidationMessages[index] = true;
        } else if (option?.values?.length < 2) {
          this.radioValidationMessages[index] = true;
        } else {
          this.radioValidationMessages[index] = false;
        }
      });
      if (this.textFieldValidationMessages.some((msg) => msg)) {
        return (this.textErrorMessage = "Empty field");
      }
      if (this.radioValidationMessages.some((msg) => msg)) {
        return (this.radioErrorMessage =
          "Some radio button fields are empty or less than 2 options.");
      } else {
        this.radioValidationMessages = [];
      }
      return null;
    },
    async getTargetUsers() {
      try {
        this.targetUsers = await getTargetUsers(true);
      } catch {
        this.alertMessage =
          "Failed to get target users. Please try again later.";
        this.alertType = "dismiss";
      }
    },
    handleMultiselectOpen() {
      if (this.targetUsers) {
        this.options = this.targetUsers.map((user) => ({
          id: user.id,
          lastName: user.lastName,
          firstName: user.firstName,
          email: user.email,
          username: user.username,
          avatarUrl: user.avatarUrl,
          domainId: user.domainId,
          chatSpaceId: user.chatSpaceId,
          isActive: user.isActive,
          chatId: user.chatId,
          name: `${user.firstName} ${user.lastName} , ${user.username}, ${user.email}`,
        }));
        this.options.unshift({ id: "selectAll", name: "Select All" });
      } else {
        this.options = [];
      }
    },
    handleMultiselectOpenForSpace() {
      if (this.spaces) {
        this.options = this.spaces.map((space) => ({
          id: space.id,
          name: space.name,
        }));
        this.options.unshift({ id: "selectAll", name: "Select All" });
      } else {
        this.options = [];
      }
    },

    handleSelect(selectedOption) {
      if (!selectedOption) {
        return;
      }
      if (selectedOption.id === "selectAll") {
        this.selectedOptions = [selectedOption];
      } else {
        const isSelectAllSelected = this.selectedOptions.some(
          (option) => option.id === "selectAll"
        );

        if (isSelectAllSelected) {
          this.selectedOptions = [selectedOption];
        } else {
          const selectedId = selectedOption.id;
          const isSelected = this.selectedOptions.some(
            (option) => option.id === selectedId
          );
          if (!isSelected) {
            this.selectedOptions.push(selectedOption);
          }
        }
      }
      if (selectedOption.id === "selectAll") {
        this.selectedOptions = [selectedOption];
      } else {
        const isSelectAllCurrentlySelected = this.selectedOptions.some(
          (option) => option.id === "selectAll"
        );

        if (isSelectAllCurrentlySelected) {
          this.selectedOptions = [selectedOption];
        }
      }
    },

    mapSelectedUsers(selectedOptions, options) {
      const selectedArray = Array.from(selectedOptions);
      const optionsArray = Array.from(options);

      const optionIds = new Set(optionsArray.map((user) => user.id));
      if (selectedArray.length == 0) {
        return (this.selectedOptionsValidation =
          "Need to select at least one user.");
      } else {
        this.selectedOptionsValidation = "";
      }

      if (selectedArray[0].id === "selectAll") {
        return optionsArray.slice(1);
      } else {
        const selectedUsers = selectedArray
          .filter((user) => optionIds.has(user.id))
          .map((user) => ({
            id: user.id,
            email: user.email,
            username: user.username,
            firstName: user.firstName,
            lastName: user.lastName,
            avatarUrl: user.avatarUrl,
            domainId: user.domainId,
            chatSpaceId: user.chatSpaceId,
            isActive: user.isActive,
            chatId: user.chatId,
          }));

        return selectedUsers;
      }
    },
    mapSelectedSpace(selectedOptions, options) {
      const selectedArray = Array.from(selectedOptions);
      const optionsArray = Array.from(options);

      const optionIds = new Set(optionsArray.map((space) => space.id));
      if (selectedArray.length == 0) {
        return (this.selectedOptionsValidation =
          "Need to select at least one space.");
      } else {
        this.selectedOptionsValidation = "";
      }

      if (selectedArray[0].id === "selectAll") {
        const idsArray = optionsArray.slice(1).map((option) => option.id);
        return idsArray;
      } else {
        const selectedSpaceIds = selectedArray
          .filter((space) => optionIds.has(space.id))
          .map((space) => space.id);

        return selectedSpaceIds;
      }
    },
    async getSpaces() {
      try {
        this.spaces = await getAllSpaces(true);
      } catch {
        this.alertMessage = "Failed to get spaces. Please try again later.";
        this.alertType = "dismiss";
      }
    },

    async submitPoll() {
      try {
        const selectedUsers = this.mapSelectedUsers(
          this.selectedOptions,
          this.options
        );
        const selectedSpace = this.mapSelectedSpace(
          this.selectedOptions,
          this.options
        );

        this.submitted = true;
        const questions = this.getStructuredQuestion(this.question);
        const payload = {
          name: this.pollName,
          isAnonymous: this.isAnonymous,
          endAt: DateTime.fromISO(this.pollEndAt, { zone: "UTC" }).toSQL({
            includeOffset: false,
          }),
          questions: questions,
        };

        if (this.pollType === "individual") {
          payload.targetUsers = selectedUsers;
        } else {
          payload.spaceIds = selectedSpace;
        }

        if (this.isScheduleCalendarSet) {
          payload.scheduledAt = this.scheduledAt;
          payload.isScheduled = this.isScheduleCalendarSet;
        }

        const scheduledAt = DateTime.fromISO(this.scheduledAt, {
          zone: "UTC",
        }).toSQL({
          includeOffset: false,
        });
        const pollEndAt = DateTime.fromISO(this.pollEndAt, {
          zone: "UTC",
        }).toSQL({
          includeOffset: false,
        });

        const validationError = await this.validatePoll(
          scheduledAt,
          pollEndAt,
          this.question
        );

        if (validationError) {
          this.alertMessage = validationError.alertMessage;
          this.alertType = validationError.alertType;
          return;
        }
        if (this.scheduledPoll) {
          payload.scheduled_expression = this.scheduledPoll;
          await createScheduledQuestionnaire(payload);
        } else {
          await createQuestionnaire(payload);
        }

        this.alertMessage = "Poll submission was successful!";
        this.alertType = "success";
        this.resetPollForm();
      } catch {
        this.alertMessage = "Poll submission failed. Please try again later.";
        this.alertType = "dismiss";
      }
    },
  },
};
</script>

<style scoped>
@import url("../css/styles/polls.css");
</style>
